import { useEffect, useRef, useState } from "react";
import { Alert, Loader, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { api } from "../api/client";
import { useScannerPrefs, type ScannerPrefs } from "../scanner/scannerPrefs";

export interface BarcodeScannerProps {
  active: boolean;
  onDetect: (rawValue: string) => void;
  /** Raw values previously scanned in the session — drawn yellow on the camera. */
  knownRaws?: Set<string>;
  /** Per-code overrides keyed by raw.trim().toUpperCase(): draws that color + label. */
  annotations?: Map<string, { color: string; label: string }>;
  /** Throttle: minimum ms between firing onDetect for the same code. Default 1500. */
  dedupeMs?: number;
  /** How often (ms) to send a frame to the backend for decoding. Default: the
   * browser's scanner setting (Settings → Scanner). */
  intervalMs?: number;
  className?: string;
  /** Called with the live video track once the camera is open (used by the
   * settings page to read zoom/torch/focus capabilities). */
  onTrackReady?: (track: MediaStreamTrack) => void;
}

type Caps = MediaTrackCapabilities & {
  torch?: boolean;
  zoom?: { min: number; max: number };
  focusMode?: string[];
  focusDistance?: { min: number; max: number };
  exposureMode?: string[];
  whiteBalanceMode?: string[];
};

function trackCaps(track: MediaStreamTrack): Caps {
  try {
    return (track.getCapabilities?.() ?? {}) as Caps;
  } catch {
    return {};
  }
}

/** Applies torch / zoom / focus / auto-exposure from the browser's scanner
 * settings, limited to what this camera reports it supports. Unsupported
 * constraints are skipped rather than sent, since iOS Safari throws on them. */
export async function applyCameraTuning(track: MediaStreamTrack, p: ScannerPrefs): Promise<void> {
  if (!("applyConstraints" in track)) return;
  const caps = trackCaps(track);
  const set: Record<string, unknown>[] = [];
  if (caps.focusMode?.includes("manual") && p.focus === "manual" && caps.focusDistance) {
    const { min, max } = caps.focusDistance;
    set.push({ focusMode: "manual", focusDistance: min + (max - min) * p.focusDistance });
  } else if (caps.focusMode?.includes("continuous")) {
    set.push({ focusMode: "continuous" });
  }
  if (caps.exposureMode?.includes("continuous")) set.push({ exposureMode: "continuous" });
  if (caps.whiteBalanceMode?.includes("continuous")) set.push({ whiteBalanceMode: "continuous" });
  if (caps.zoom) {
    const z = p.zoom > 0 ? p.zoom : caps.zoom.min;
    set.push({ zoom: Math.min(caps.zoom.max, Math.max(caps.zoom.min, z)) });
  }
  if (caps.torch) set.push({ torch: p.torch });
  for (const c of set) {
    try {
      await track.applyConstraints({ advanced: [c as MediaTrackConstraintSet] });
    } catch {
      // This one isn't honoured on this device — keep applying the rest.
    }
  }
}

const RES_DIMS: Record<number, [number, number]> = {
  720: [1280, 720],
  1080: [1920, 1080],
  1440: [2560, 1440],
};

/**
 * Camera scanner that decodes QR codes via the backend `/scan/decode` endpoint
 * (OpenCV `detectAndDecodeMulti`). Cross-device reliable — does not depend on
 * the browser's `BarcodeDetector` (which is buggy on Android Chrome and absent
 * on iOS Safari).
 *
 * Renders <video> + an overlay <canvas> that draws a polygon around every
 * QR detected in the most recent frame:
 *   - Green: code not seen in this session yet
 *   - Yellow: already in `knownRaws`
 */
/* Frame size, JPEG quality, interval and request timeout come from the
 * browser's scanner settings (scanner/scannerPrefs.ts). The abandon-after
 * timeout keeps one stalled request on a flaky mobile connection from
 * freezing scanning. */
/** Video frames not advancing for this long means the camera stalled
 * (screen lock, app switch, another app grabbed it) — reopen it. */
const STALL_RESTART_MS = 4000;

export function BarcodeScanner({
  active,
  onDetect,
  knownRaws,
  annotations,
  dedupeMs = 1500,
  intervalMs: intervalMsProp,
  className,
  onTrackReady,
}: BarcodeScannerProps) {
  const { t } = useTranslation();
  const [prefs] = useScannerPrefs();
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const intervalMs = intervalMsProp ?? prefs.intervalMs;
  const streamRef = useRef<MediaStream | null>(null);
  const onTrackReadyRef = useRef(onTrackReady);
  onTrackReadyRef.current = onTrackReady;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const overlayRef = useRef<HTMLCanvasElement | null>(null);
  const captureRef = useRef<HTMLCanvasElement | null>(null);
  const lastEmittedRef = useRef<Map<string, number>>(new Map());
  // Stored as upper-cased / trimmed keys so the comparison matches what the
  // parent stores in its dedup set, regardless of how the decoder cased the
  // bytes.
  const knownRef = useRef<Set<string>>(new Set());
  const annoRef = useRef<Map<string, { color: string; label: string }>>(new Map());
  const refocusRef = useRef<((xRel: number, yRel: number) => void) | null>(null);
  useEffect(() => {
    const next = new Set<string>();
    if (knownRaws) for (const k of knownRaws) next.add(k.trim().toUpperCase());
    knownRef.current = next;
  }, [knownRaws]);
  useEffect(() => {
    annoRef.current = annotations ?? new Map();
  }, [annotations]);

  // Keep a stable reference to the latest onDetect / dedupeMs so the camera
  // effect doesn't tear down on every parent re-render (which would happen
  // every time the parent's state changes, since onDetect is usually inline).
  const onDetectRef = useRef(onDetect);
  useEffect(() => {
    onDetectRef.current = onDetect;
  }, [onDetect]);
  const dedupeMsRef = useRef(dedupeMs);
  useEffect(() => {
    dedupeMsRef.current = dedupeMs;
  }, [dedupeMs]);
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const [status, setStatus] = useState<"starting" | "running" | "error">("starting");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return;
    let timer: number | null = null;
    let cancelled = false;
    let inFlight = false;
    let opening = false;
    let lastVideoTime = -1;
    let lastAdvanceAt = 0;

    function stopStream() {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    async function openStream(): Promise<MediaStream> {
      const p = prefsRef.current;
      const [w, h] = RES_DIMS[p.resolution] ?? RES_DIMS[1080];
      const size = { width: { ideal: w }, height: { ideal: h } };
      if (p.deviceId) {
        try {
          return await navigator.mediaDevices.getUserMedia({
            video: { deviceId: { exact: p.deviceId }, ...size },
            audio: false,
          });
        } catch (err) {
          // The saved camera is gone (device ids can rotate) — fall back to
          // facing mode instead of leaving the scanner dead. A permission
          // refusal is final, so surface it.
          if (err instanceof DOMException && err.name === "NotAllowedError") throw err;
        }
      }
      return navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: p.facing }, ...size },
        audio: false,
      });
    }

    async function start() {
      const video = videoRef.current;
      if (!video || opening) return;
      opening = true;
      stopStream();
      try {
        const stream = await openStream();
        if (cancelled) {
          // A newer run may own streamRef by now — stop only our own stream.
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        // Android ends the track when the screen locks or another app takes
        // the camera; reopen as soon as the page is visible again.
        if (track) {
          track.addEventListener("ended", () => void restartIfVisible());
          await applyCameraTuning(track, prefsRef.current);
          onTrackReadyRef.current?.(track);
        }
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        await video.play();
        lastVideoTime = -1;
        lastAdvanceAt = Date.now();
        setStatus("running");
        if (timer === null) timer = window.setInterval(scanOnce, intervalMs);
      } catch (err) {
        if (cancelled) return;
        setError(tRef.current("barcodeScanner.cameraUnavailable", { error: String(err).slice(0, 120) }));
        setStatus("error");
      } finally {
        opening = false;
      }
    }

    function restartIfVisible() {
      if (cancelled || document.visibilityState !== "visible") return;
      void start();
    }

    function onVisibilityChange() {
      if (document.visibilityState !== "visible") return;
      const track = streamRef.current?.getVideoTracks()[0];
      const video = videoRef.current;
      if (!track || track.readyState !== "live") {
        restartIfVisible();
      } else if (video && video.paused) {
        // Track survived but playback was suspended in the background.
        video.play().catch(() => restartIfVisible());
      }
      lastAdvanceAt = Date.now();
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    /** Tap-to-focus: nudge the camera to re-acquire focus on a specific
     * point. Browsers without pointsOfInterest support fall back to a
     * mode toggle, which still forces a refocus pass on most devices. */
    async function refocusAt(xRel: number, yRel: number) {
      const track = streamRef.current?.getVideoTracks()[0];
      if (!track || !("applyConstraints" in track)) return;
      try {
        await track.applyConstraints({
          advanced: [
            {
              pointsOfInterest: [{ x: xRel, y: yRel }],
              focusMode: "single-shot",
            } as MediaTrackConstraintSet,
          ],
        });
      } catch {
        // pointsOfInterest unsupported — a manual/auto toggle still forces
        // a refocus pass on most devices.
        try {
          await track.applyConstraints({
            advanced: [{ focusMode: "manual" } as MediaTrackConstraintSet],
          });
        } catch {
          // give up silently
        }
      }
      // Hand control back to the configured focus mode after the pulse.
      window.setTimeout(() => {
        if (streamRef.current) void applyCameraTuning(track, prefsRef.current);
      }, 400);
    }
    refocusRef.current = refocusAt;

    async function scanOnce() {
      if (inFlight || opening || document.visibilityState !== "visible") return;
      const video = videoRef.current;
      const overlay = overlayRef.current;
      const capture = captureRef.current;
      if (!video || !overlay || !capture) return;

      // Stall watchdog: a frozen picture means the camera died silently.
      const nowTs = Date.now();
      if (video.currentTime !== lastVideoTime) {
        lastVideoTime = video.currentTime;
        lastAdvanceAt = nowTs;
      } else if (nowTs - lastAdvanceAt > STALL_RESTART_MS) {
        lastAdvanceAt = nowTs;
        void start();
        return;
      }
      if (video.videoWidth === 0 || video.videoHeight === 0) return;

      // Match overlay canvas to displayed video size for correct polygon mapping
      const rect = video.getBoundingClientRect();
      const dispW = Math.max(1, Math.round(rect.width));
      const dispH = Math.max(1, Math.round(rect.height));
      if (overlay.width !== dispW || overlay.height !== dispH) {
        overlay.width = dispW;
        overlay.height = dispH;
      }

      // Capture frame, downscaled so the longest side is at most SCAN_MAX_SIDE.
      // Reduces JPEG encode + transit + server decode time without hurting QR
      // detection (≥720px keeps codes legible even when small in frame).
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const scale = Math.min(1, prefsRef.current.maxSide / Math.max(vw, vh));
      capture.width = Math.max(1, Math.round(vw * scale));
      capture.height = Math.max(1, Math.round(vh * scale));
      const cctx = capture.getContext("2d");
      if (!cctx) return;
      cctx.drawImage(video, 0, 0, capture.width, capture.height);

      // Encode to binary JPEG + send. Binary (image/jpeg) avoids the ~33%
      // base64 overhead and the synchronous toDataURL call.
      inFlight = true;
      let results: { data: string; polygon: [number, number][] }[] = [];
      const abort = new AbortController();
      const abortTimer = window.setTimeout(() => abort.abort(), prefsRef.current.timeoutMs);
      try {
        const blob: Blob | null = await new Promise((resolve) =>
          capture.toBlob(resolve, "image/jpeg", prefsRef.current.quality),
        );
        if (blob) {
          const resp = await api.scanDecodeBlob(blob, abort.signal);
          results = resp.results;
        }
      } catch {
        // network/decode hiccup or timeout — try again next tick
      } finally {
        window.clearTimeout(abortTimer);
        inFlight = false;
      }
      if (cancelled) return;

      // Draw overlay
      const octx = overlay.getContext("2d");
      if (!octx) return;
      octx.clearRect(0, 0, overlay.width, overlay.height);

      const now = Date.now();
      for (const r of results) {
        const key = r.data.trim().toUpperCase();
        const seen = knownRef.current.has(key);
        const ann = annoRef.current.get(key);
        const stroke = ann ? ann.color : seen ? "rgba(255, 196, 0, 1)" : "rgba(46, 175, 84, 1)";
        const fill = ann
          ? ann.color.replace("1)", "0.18)")
          : seen ? "rgba(255, 196, 0, 0.18)" : "rgba(46, 175, 84, 0.18)";

        octx.lineWidth = 4;
        octx.strokeStyle = stroke;
        octx.fillStyle = fill;
        octx.beginPath();
        r.polygon.forEach(([xRel, yRel], i) => {
          const x = xRel * overlay.width;
          const y = yRel * overlay.height;
          if (i === 0) octx.moveTo(x, y);
          else octx.lineTo(x, y);
        });
        octx.closePath();
        octx.fill();
        octx.stroke();

        // Label the code at the top-left corner
        if (r.polygon.length > 0) {
          const [x0Rel, y0Rel] = r.polygon[0];
          const x = x0Rel * overlay.width;
          const y = y0Rel * overlay.height;
          const label = ann
            ? ann.label
            : r.data.length > 24 ? r.data.slice(0, 24) + "…" : r.data;
          octx.font = "bold 12px sans-serif";
          const tw = octx.measureText(label).width;
          octx.fillStyle = "rgba(0, 0, 0, 0.7)";
          octx.fillRect(x, Math.max(0, y - 18), tw + 10, 18);
          octx.fillStyle = stroke;
          octx.fillText(label, x + 5, Math.max(12, y - 5));
        }

        // If the parent already has this code in its session, skip emit
        // entirely — saves an API call and avoids flapping the counter when
        // older entries fall out of the parent's display table.
        if (seen) continue;

        // Per-code emit throttle
        const last = lastEmittedRef.current.get(key) ?? 0;
        if (now - last > dedupeMsRef.current) {
          lastEmittedRef.current.set(key, now);
          onDetectRef.current(r.data);
        }
      }
    }

    void start();
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (timer !== null) window.clearInterval(timer);
      stopStream();
      const v = videoRef.current;
      if (v) v.srcObject = null;
      refocusRef.current = null;
    };
  }, [active, intervalMs, prefs.deviceId, prefs.facing, prefs.resolution]);

  // Torch / zoom / focus change live without reopening the camera.
  useEffect(() => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (track && track.readyState === "live") void applyCameraTuning(track, prefsRef.current);
  }, [prefs.torch, prefs.zoom, prefs.focus, prefs.focusDistance]);

  function handleVideoTap(e: React.PointerEvent<HTMLVideoElement>) {
    const video = e.currentTarget;
    const rect = video.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    refocusRef.current?.(x, y);
  }

  if (!active) return null;

  if (error) {
    return (
      <Alert color="red" variant="light">
        {error}
      </Alert>
    );
  }

  return (
    <div className={className} style={{ position: "relative", width: "100%" }}>
      <video
        ref={videoRef}
        playsInline
        muted
        onPointerDown={handleVideoTap}
        style={{
          width: "100%",
          display: "block",
          borderRadius: 4,
          cursor: "crosshair",
        }}
      />
      <canvas
        ref={overlayRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      />
      <canvas ref={captureRef} style={{ display: "none" }} />
      {status === "starting" && (
        <div
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            background: "rgba(0,0,0,0.6)",
            color: "white",
            padding: "4px 8px",
            borderRadius: 4,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Loader size="xs" color="gray.0" />
          <Text size="xs" c="white">
            {t("barcodeScanner.starting")}
          </Text>
        </div>
      )}
    </div>
  );
}
